// CrmAssistantMount.tsx — the ONLY seam between the CRM shell and the Rheora AI
// module (epic FE-CRM-04).
//
// ─── WHY THIS FILE EXISTS AT ALL ───────────────────────────────────────────
// `CrmShell` imports shell-level things: the rail, the skeleton, the user menu.
// A shell that imports one feature module is the start of a shell that imports
// six, and the cross-module-import ban exists precisely because that is how a
// country app turns into a ball of mud. So the shell imports exactly one file —
// this one — and this is the single file to delete to remove the feature.
//
// ─── THE TWO GATES ─────────────────────────────────────────────────────────
// Both must pass. `aiAssistantEnabled` is the feature flag and defaults false,
// so the widget ships dark. `aiChatApiUrl !== ''` is the backstop, and it is not
// redundant: `env.aiChatApiUrl` deliberately defaults to an empty string, so a
// flag-on build in an environment that forgot the url would otherwise resolve a
// relative path against the CRM's own origin — and Vite answers 200 with
// `index.html` for any unresolved path, so every probe would report success
// while every question 404'd forever. Requiring both turns a misconfiguration
// into an absent widget, which is a safe failure.
//
// ─── WHY THE PROVIDER IS NOT LAZY (and the dock/launcher are) ──────────────
// The provider has to be an ancestor of both the docked panel (a grid child of
// `.crm-body`) and the floating launcher (a fixed-position sibling), which means
// it wraps the shell. A `React.lazy` provider would put the ENTIRE CRM behind a
// Suspense boundary and flash the whole app on first paint while a chunk for an
// off-by-default feature downloads. That trade is not worth it, so the provider
// is a static import and the two *visual* pieces — which are the bulk of the
// code — are lazy.
//
// Flag-off cost is therefore the provider module, not zero: the panel, the
// markdown tokeniser and the sixteen components are never requested because the
// components that would trigger their import return `null` first. Be precise
// about that rather than claiming a code-split that is not there.

import { Suspense, lazy, type ReactNode } from 'react';

import { env } from '../../config/env';
import { AiAssistantProvider } from '../../module/ai';

/**
 * Evaluated once at module scope, not per render.
 *
 * A runtime flag cannot be tree-shaken, so this does not remove the provider
 * from the bundle — it removes the *work*, and it stops the lazy chunks from
 * ever being requested. See the header.
 */
const AI_ASSISTANT_ENABLED = env.aiAssistantEnabled && env.aiChatApiUrl !== '';

const LazyAiAssistantDock = lazy(async () => {
  const mod = await import('../../module/ai');
  return { default: mod.AiAssistantDock };
});

const LazyAiAssistantLauncher = lazy(async () => {
  const mod = await import('../../module/ai');
  return { default: mod.AiAssistantLauncher };
});

/**
 * Wraps the shell so the conversation outlives route changes.
 *
 * When the feature is off this is a pass-through: no context, no reducer, no
 * abort controller, no hooks. That matters beyond performance — an inert
 * provider cannot hold a stale tenant or a dangling request.
 */
export function CrmAssistantProvider({ children }: { children: ReactNode }): JSX.Element {
  if (!AI_ASSISTANT_ENABLED) return <>{children}</>;
  return <AiAssistantProvider>{children}</AiAssistantProvider>;
}

/**
 * The panel. Renders as the THIRD grid child of `.crm-body`, so its presence
 * creates the column and its absence removes it — the DOM is the state.
 *
 * `fallback={null}` rather than a skeleton: this mounts in response to a click,
 * the chunk is small, and a skeleton column that appears and then reflows is
 * worse than the panel simply arriving.
 */
export function CrmAssistantDock(): JSX.Element | null {
  if (!AI_ASSISTANT_ENABLED) return null;
  return (
    <Suspense fallback={null}>
      <LazyAiAssistantDock />
    </Suspense>
  );
}

/**
 * The floating launcher. Sits in the bottom-right slot, which is free — the only
 * other FAB in the app (`theme/tweaks/TweaksTrigger`) is exported but mounted
 * nowhere.
 */
export function CrmAssistantLauncher(): JSX.Element | null {
  if (!AI_ASSISTANT_ENABLED) return null;
  return (
    <Suspense fallback={null}>
      <LazyAiAssistantLauncher />
    </Suspense>
  );
}
